/**
 * StudioPagePublishDialog.tsx — Publicação de página pública (landing page)
 *
 * Coleta o endereço público e os dados de busca/compartilhamento antes de
 * publicar. A montagem do HTML final acontece no servidor.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Páginas públicas, Fase 2)
 */

import { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import type { StudioPublication } from '@/lib/studio/api';

interface Props {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Publicação atual, quando a página já esteve no ar. */
  publicacao?: StudioPublication | null;
  /** Nome do modelo, usado como título padrão. */
  nomeModelo: string;
  publicando: boolean;
  onConfirm: (dados: {
    path: string;
    seo: {
      title: string;
      description: string;
      image_url: string;
      canonical_url: string;
      indexable: boolean;
    };
  }) => void;
}

/** Deixa o endereço no formato aceito: minúsculo, sem acento e com hífen. */
function normalizarEndereco(valor: string): string {
  return valor
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\-/]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-/]+|[-/]+$/g, '');
}

export function StudioPagePublishDialog({
  open,
  onOpenChange,
  publicacao,
  nomeModelo,
  publicando,
  onConfirm,
}: Props) {
  const [endereco, setEndereco] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [imagem, setImagem] = useState('');
  const [canonica, setCanonica] = useState('');
  const [indexavel, setIndexavel] = useState(true);

  // Recarrega os campos sempre que o painel é aberto
  useEffect(() => {
    if (!open) return;
    const seo = publicacao?.seo_json ?? {};
    setEndereco(publicacao?.path ?? normalizarEndereco(nomeModelo));
    setTitulo(String(seo.title ?? nomeModelo));
    setDescricao(String(seo.description ?? ''));
    setImagem(String(seo.image_url ?? ''));
    setCanonica(String(seo.canonical_url ?? ''));
    setIndexavel(seo.indexable !== false);
  }, [open, publicacao, nomeModelo]);

  const enderecoValido = /^[a-z0-9][a-z0-9\-/]{0,80}$/.test(endereco);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Publicar página</DialogTitle>
          <DialogDescription>
            Escolha o endereço do link e como a página aparece em buscas e ao ser compartilhada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="studio-page-endereco">Endereço do link</Label>
            <Input
              id="studio-page-endereco"
              value={endereco}
              onChange={(e) => setEndereco(normalizarEndereco(e.target.value))}
              placeholder="promocao-frota"
            />
            <p className="text-xs text-muted-foreground">
              Somente letras minúsculas, números e hífen.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="studio-page-titulo">Título</Label>
            <Input
              id="studio-page-titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              maxLength={120}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="studio-page-descricao">Descrição</Label>
            <Textarea
              id="studio-page-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={300}
              rows={2}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="studio-page-imagem">Imagem de compartilhamento</Label>
              <Input
                id="studio-page-imagem"
                value={imagem}
                onChange={(e) => setImagem(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="studio-page-canonica">Endereço preferencial</Label>
              <Input
                id="studio-page-canonica"
                value={canonica}
                onChange={(e) => setCanonica(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <p className="text-sm font-medium">Aparecer em buscas</p>
              <p className="text-xs text-muted-foreground">
                Desligue para manter a página acessível apenas por quem tem o link.
              </p>
            </div>
            <Switch checked={indexavel} onCheckedChange={setIndexavel} />
          </div>

          {publicacao?.status === 'published' && (
            <InlineNotice variant="info">
              Ao publicar, quem abrir o link passa a ver esta versão.
            </InlineNotice>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!enderecoValido || publicando}
            onClick={() =>
              onConfirm({
                path: endereco,
                seo: {
                  title: titulo,
                  description: descricao,
                  image_url: imagem,
                  canonical_url: canonica,
                  indexable: indexavel,
                },
              })
            }
            className="gap-1.5"
          >
            {publicando ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Publicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
